import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons';
import { StateBadge } from '../components/StateBadge';
import { GlassCard } from '../components/GlassCard';
import { ContactForm } from '../components/ContactForm';
import { Toast } from '../components/Toast';
import { api, ApiError } from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import { T } from '../lib/design-tokens';
import type { TaskRecord } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export function TaskApprovalPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const pendingTask = useTaskStore((s) => s.pendingTask);
  const clearPendingTask = useTaskStore((s) => s.clearPendingTask);

  // Editable fields
  const [talkingPoints, setTalkingPoints] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Contact form
  const [contactInfo, setContactInfo] = useState<{ name: string; phone: string; email?: string } | null>(null);

  // State
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [fetchedTask, setFetchedTask] = useState<TaskRecord | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);

  // Determine data source: pending task from store or fetched
  const hasPending = pendingTask && pendingTask.taskId === id;
  const classification = hasPending ? pendingTask.classification : null;
  const plan = hasPending ? pendingTask.plan : null;
  const requiresContact = hasPending ? pendingTask.requiresContact : false;
  const requiresPhoneAuth = hasPending ? pendingTask.requiresPhoneAuth : false;
  const requiresEmailAuth = hasPending ? pendingTask.requiresEmailAuth : false;
  const channel = classification?.channel ?? fetchedTask?.channel ?? 'voice';
  const goal = classification?.goal ?? fetchedTask?.goal ?? '';
  const company = classification?.company ?? fetchedTask?.company ?? null;
  const confidence = classification?.confidence ?? fetchedTask?.confidence ?? 0;
  const intent = classification?.intent ?? fetchedTask?.intent ?? '';

  // Fetch task if no pending data in store
  useEffect(() => {
    if (hasPending) {
      setTalkingPoints(plan?.talkingPoints ?? '');
      setEmailSubject(plan?.emailSubject ?? '');
      setEmailBody(plan?.emailBody ?? '');
      return;
    }
    if (!id) return;

    setFetchLoading(true);
    api<{ task: TaskRecord }>(`/tasks/${id}/evidence`)
      .then((data) => {
        const t = data.task;
        setFetchedTask(t);
        setTalkingPoints(t.talkingPoints ?? '');
        setEmailSubject(t.emailSubject ?? '');
        setEmailBody(t.emailBody ?? '');
      })
      .catch(() => {
        setToast('Failed to load task');
      })
      .finally(() => setFetchLoading(false));
  }, [id, hasPending, plan]);

  const handleApprove = async () => {
    if (!id || loading) return;
    setLoading(true);

    try {
      const body: Record<string, unknown> = { approved: true };
      if (talkingPoints && talkingPoints !== (plan?.talkingPoints ?? '')) {
        body.talking_points = talkingPoints;
      }
      if (emailBody && emailBody !== (plan?.emailBody ?? '')) {
        body.email_body = emailBody;
      }
      if (contactInfo) {
        body.contact_info = { name: contactInfo.name, phone: contactInfo.phone, email: contactInfo.email };
      }

      await api(`/task/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      clearPendingTask();
      setToast('Approved. Executing in 10 seconds.');
      setTimeout(() => navigate(`/operations/${id}`), 800);
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Failed to approve task');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id || loading) return;
    setLoading(true);

    try {
      await api(`/task/${id}/cancel`, { method: 'POST' });
      clearPendingTask();
      setToast('Task cancelled.');
      setTimeout(() => navigate('/'), 800);
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Failed to cancel task');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="max-w-[760px] mx-auto" style={{ padding: '48px 40px' }}>
        <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream35 }}>Loading...</div>
      </div>
    );
  }

  const confidenceColor = confidence >= 0.9 ? T.green : confidence >= 0.7 ? T.cream : T.amber;

  const inputBaseStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: `1.5px solid ${T.glassBorder}`,
    fontFamily: T.sans,
    fontSize: 14,
    lineHeight: 1.6,
    color: T.cream,
    outline: 'none',
    resize: 'vertical',
    background: T.surface,
    transition: 'border-color 0.2s',
  };

  return (
    <div className="max-w-[760px] mx-auto" style={{ padding: '48px 40px' }}>
      <button
        type="button"
        onClick={() => navigate('/')}
        className="flex items-center gap-1.5 border-none bg-transparent cursor-pointer p-0 mb-8"
        style={{ fontSize: 12, color: T.cream35, fontFamily: T.mono, letterSpacing: '0.05em', textTransform: 'uppercase' }}
      >
        <ArrowLeftIcon size={16} />
        Back
      </button>

      <div className="mb-8">
        <StateBadge state="AWAITING_APPROVAL" size="lg" />
        <h1
          style={{
            fontFamily: T.serif,
            fontSize: 28,
            fontWeight: 400,
            color: T.cream,
            lineHeight: 1.2,
            marginTop: 12,
            letterSpacing: '-0.5px',
          }}
        >
          {goal || 'Review task'}
        </h1>
        <div style={{ fontFamily: T.sans, marginTop: 8, fontSize: 13, color: T.cream35 }}>
          {[company, channel, 'Just now'].filter(Boolean).join(' \u00B7 ')}
        </div>
      </div>

      <GlassCard style={{ padding: '24px 28px', marginBottom: 20 }}>
        <h3
          style={{
            fontFamily: T.mono,
            fontSize: 10,
            fontWeight: 600,
            color: T.cream20,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            marginBottom: 16,
          }}
        >
          Classification
        </h3>
        <div className="grid grid-cols-2 gap-y-3 gap-x-8">
          <div>
            <span className="text-xs block mb-0.5" style={{ color: T.cream35, fontFamily: T.sans }}>Intent</span>
            <div style={{ fontFamily: T.sans, fontWeight: 500, fontSize: 14, color: T.cream }}>{intent}</div>
          </div>
          <div>
            <span className="text-xs block mb-0.5" style={{ color: T.cream35, fontFamily: T.sans }}>Channel</span>
            <div style={{ fontFamily: T.sans, fontWeight: 500, fontSize: 14, color: T.cream }}>{channel}</div>
          </div>
          <div>
            <span className="text-xs block mb-0.5" style={{ color: T.cream35, fontFamily: T.sans }}>Company</span>
            <div style={{ fontFamily: T.sans, fontWeight: 500, fontSize: 14, color: T.cream }}>{company ?? '—'}</div>
          </div>
          <div>
            <span className="text-xs block mb-0.5" style={{ color: T.cream35, fontFamily: T.sans }}>Confidence</span>
            <div style={{ fontFamily: T.mono, fontWeight: 700, fontSize: 14, color: confidenceColor }}>
              {Math.round(confidence * 100)}%
            </div>
          </div>
        </div>
      </GlassCard>

      <GlassCard style={{ padding: '24px 28px', marginBottom: 20 }}>
        <h3
          style={{
            fontFamily: T.mono,
            fontSize: 10,
            fontWeight: 600,
            color: T.cream20,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            marginBottom: 16,
          }}
        >
          Plan Preview
        </h3>

        {(plan?.contactName || fetchedTask?.contactName) && (
          <div className="mb-4">
            <span className="text-xs block mb-1" style={{ color: T.cream35, fontFamily: T.sans }}>Contact</span>
            <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream }}>
              {plan?.contactName ?? fetchedTask?.contactName}
              {(plan?.contactPhone ?? fetchedTask?.contactPhone) && (
                <span style={{ color: T.cream35 }}> ({plan?.contactPhone ?? fetchedTask?.contactPhone})</span>
              )}
            </div>
            {(plan?.contactSource ?? fetchedTask?.contactSource) && (
              <span
                className="inline-block text-xs font-medium mt-1"
                style={{
                  padding: '2px 8px',
                  borderRadius: 100,
                  background: T.cream04,
                  color: T.cream35,
                  fontFamily: T.sans,
                }}
              >
                {plan?.contactSource ?? fetchedTask?.contactSource}
              </span>
            )}
          </div>
        )}

        {channel === 'voice' && (
          <div>
            <label className="text-xs font-medium block mb-1.5" style={{ color: T.cream60, fontFamily: T.sans }}>
              Talking Points
            </label>
            <textarea
              value={talkingPoints}
              onChange={(e) => setTalkingPoints(e.target.value)}
              rows={4}
              style={inputBaseStyle}
              onFocus={(e) => { e.currentTarget.style.borderColor = T.gold; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = T.glassBorder; }}
            />
          </div>
        )}

        {channel === 'email' && (
          <>
            <div className="mb-3">
              <label className="text-xs font-medium block mb-1.5" style={{ color: T.cream60, fontFamily: T.sans }}>
                Subject
              </label>
              <input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                style={{ ...inputBaseStyle, lineHeight: 1.4 }}
                onFocus={(e) => { e.currentTarget.style.borderColor = T.gold; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = T.glassBorder; }}
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1.5" style={{ color: T.cream60, fontFamily: T.sans }}>
                Email Body
              </label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={6}
                style={inputBaseStyle}
                onFocus={(e) => { e.currentTarget.style.borderColor = T.gold; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = T.glassBorder; }}
              />
            </div>
          </>
        )}
      </GlassCard>

      {requiresContact && (
        <div className="mb-5">
          <ContactForm
            channel={channel ?? 'voice'}
            onSubmit={(contact) => setContactInfo(contact)}
            initialValues={{
              name: plan?.contactName ?? undefined,
              phone: plan?.contactPhone ?? undefined,
            }}
          />
          {contactInfo && (
            <div className="text-xs mt-2" style={{ color: T.green, fontFamily: T.sans }}>
              Contact saved: {contactInfo.name}
            </div>
          )}
        </div>
      )}

      {requiresPhoneAuth && (
        <GlassCard
          style={{
            padding: '20px 24px',
            marginBottom: 20,
            border: `1px solid ${T.amberDim}`,
            background: T.amberDim,
          }}
        >
          <div className="text-sm mb-3" style={{ color: T.cream60, fontFamily: T.sans }}>
            You need to verify your phone number before voice tasks can execute.
          </div>
          <button
            type="button"
            style={{
              padding: '8px 18px',
              borderRadius: 8,
              border: 'none',
              background: T.amber,
              color: T.bg,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: T.sans,
            }}
          >
            Verify Phone Number
          </button>
        </GlassCard>
      )}

      {requiresEmailAuth && (
        <GlassCard
          style={{
            padding: '20px 24px',
            marginBottom: 20,
            border: `1px solid ${T.amberDim}`,
            background: T.amberDim,
          }}
        >
          <div className="text-sm mb-3" style={{ color: T.cream60, fontFamily: T.sans }}>
            Connect your Gmail account to send emails.
          </div>
          <button
            type="button"
            onClick={() => window.open(`${API_BASE_URL}/v1/auth/gmail`, '_blank')}
            style={{
              padding: '8px 18px',
              borderRadius: 8,
              border: 'none',
              background: T.amber,
              color: T.bg,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: T.sans,
            }}
          >
            Connect Gmail
          </button>
        </GlassCard>
      )}

      <div className="flex items-center gap-3 mt-8">
        <button
          type="button"
          onClick={handleApprove}
          disabled={loading || (requiresContact && !contactInfo)}
          style={{
            padding: '9px 24px',
            borderRadius: 10,
            border: 'none',
            background: loading || (requiresContact && !contactInfo) ? T.cream20 : T.gold,
            color: T.bg,
            fontSize: 13,
            fontWeight: 700,
            cursor: loading || (requiresContact && !contactInfo) ? 'default' : 'pointer',
            fontFamily: T.mono,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {loading ? 'Approving...' : 'Approve'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={loading}
          style={{
            padding: '9px 24px',
            borderRadius: 10,
            border: `1px solid ${T.cream12}`,
            background: 'transparent',
            color: T.cream60,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: T.sans,
          }}
        >
          Cancel
        </button>
      </div>

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
